import type {
  ChatAutoSelection,
  ChatExecutedQuery,
  ChatOrchestrationWebSocketPayload,
} from "../../../services/chatService";

/** Roles allowed to save chat turns as examples (mirrors backend `/chat-query-examples` POST rule). */
export const EXAMPLE_SAVE_ROLES = ["admin", "tenant_admin", "user", "reporter"] as const;

export interface ExampleSourceMessage {
  role: string;
  error?: unknown;
  payload?: ChatOrchestrationWebSocketPayload;
  historyResponseType?: string;
}

const SAVABLE_TYPES = ["chart", "table", "json", "html", "widget-draft"];

export function messageResponseType(m: ExampleSourceMessage): string {
  return String(m.payload?.type || m.historyResponseType || "").toLowerCase();
}

/** Queries behind an assistant answer: executed queries, else the widget draft's OpenSearch DSL. */
export function extractExampleQueries(m: ExampleSourceMessage): ChatExecutedQuery[] {
  const fromPayload = m.payload?.executedQueries?.filter((q) => !q.async);
  if (Array.isArray(fromPayload) && fromPayload.length > 0) {
    return fromPayload;
  }
  const draft = m.payload?.widgetDraft;
  if (draft && typeof draft === "object") {
    const ds = (draft as Record<string, unknown>).dataSource;
    if (ds && typeof ds === "object") {
      const rec = ds as Record<string, unknown>;
      const language = String(rec.queryLanguage || "").toUpperCase();
      if (typeof rec.adaptorId === "string" && rec.queryBody != null && (language === "SQL" || language === "MONGO")) {
        const adaptor = m.payload?.autoSelection?.adaptors?.find((a) => a.adaptorId === rec.adaptorId);
        return [
          {
            kind: language === "SQL" ? "sql" : "mongo",
            tool: "Adaptor_Query",
            adaptorId: rec.adaptorId,
            adaptorName: adaptor?.name,
            adaptorType: adaptor?.type,
            query: rec.queryBody,
          },
        ];
      }
      const indices = Array.isArray(rec.indices) ? (rec.indices as unknown[]) : [];
      const indexName =
        typeof rec.indexName === "string"
          ? rec.indexName
          : typeof indices[0] === "string"
            ? (indices[0] as string)
            : undefined;
      if (rec.dslTemplate != null && indexName) {
        return [
          { kind: "opensearch", tool: "OpenSearch", indexName, searchType: "_search", query: rec.dslTemplate },
        ];
      }
    }
  }
  return [];
}

/**
 * Any successful assistant answer that ran a query can be saved — plain text answers included.
 * Rich outputs stay savable (disabled with a hint) so the button is discoverable.
 */
export function canSaveAsExample(m: ExampleSourceMessage): boolean {
  if (m.role !== "assistant" || m.error) return false;
  if (extractExampleQueries(m).length > 0) return true;
  return SAVABLE_TYPES.includes(messageResponseType(m));
}

/** Warehouse queries of this answer that still run (or ran) as background jobs. */
export function asyncWarehouseJobs(m: ExampleSourceMessage): ChatExecutedQuery[] {
  const queries = m.payload?.executedQueries;
  return Array.isArray(queries) ? queries.filter((q) => q.async && typeof q.jobId === "string") : [];
}

export function exampleTools(queries: ChatExecutedQuery[]): string[] {
  const out = new Set<string>();
  for (const q of queries) {
    out.add(q.tool || (q.kind === "opensearch" ? "OpenSearch" : "Adaptor_Query"));
  }
  return Array.from(out);
}

/** Resource selection that won the turn (only the parts the saved queries actually used). */
export function exampleResources(
  queries: ChatExecutedQuery[],
  autoSelection?: ChatAutoSelection
): Record<string, unknown> | undefined {
  const indices = Array.from(
    new Set(queries.filter((q) => q.kind === "opensearch" && q.indexName).map((q) => q.indexName as string))
  );
  const adaptors = Array.from(
    new Map(
      queries
        .filter((q) => q.kind !== "opensearch" && q.adaptorId)
        .map((q) => [q.adaptorId as string, { adaptorId: q.adaptorId, name: q.adaptorName, type: q.adaptorType }])
    ).values()
  );
  if (indices.length === 0 && adaptors.length === 0) return undefined;
  return {
    ...(autoSelection?.mode ? { mode: autoSelection.mode } : {}),
    ...(indices.length ? { indices } : {}),
    ...(adaptors.length ? { adaptors } : {}),
  };
}

export function queryToText(q: ChatExecutedQuery): string {
  if (typeof q.query === "string") return q.query;
  try {
    return JSON.stringify(q.query, null, 2);
  } catch {
    return String(q.query);
  }
}
